import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { OCCUPATIONS, SUBSCRIPTION_PLANS, type CreateOnboardingInput } from '@scriptpad/shared';
import { createOnboarding } from '../api/client.js';

interface FormValues {
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  occupation: string;
  ahpraNumber: string;
  prescriberNumber: string;
  primaryPracticeName: string;
  plan: string;
  ttlDays: number;
  sendEmail: boolean;
}

const defaults: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  contactNumber: '',
  occupation: '',
  ahpraNumber: '',
  prescriberNumber: '',
  primaryPracticeName: '',
  plan: '',
  ttlDays: 30,
  sendEmail: true,
};

export const NewOnboardingPage = () => {
  const navigate = useNavigate();
  const { control, handleSubmit, formState } = useForm<FormValues>({ defaultValues: defaults });

  const mutation = useMutation({
    mutationFn: (input: CreateOnboardingInput) => createOnboarding(input),
    onSuccess: (created) => navigate(`/onboardings/${created._id}`),
  });

  const onSubmit = (v: FormValues): void => {
    mutation.mutate({
      ttlDays: v.ttlDays,
      sendEmail: v.sendEmail,
      invitee: {
        firstName: v.firstName.trim(),
        lastName: v.lastName.trim(),
        email: v.email.trim().toLowerCase(),
        contactNumber: v.contactNumber.trim() || undefined,
        occupation: (v.occupation as (typeof OCCUPATIONS)[number]) || undefined,
        ahpraNumber: v.ahpraNumber.trim() || undefined,
        prescriberNumber: v.prescriberNumber.trim() || undefined,
        primaryPracticeName: v.primaryPracticeName.trim() || undefined,
        plan: (v.plan as (typeof SUBSCRIPTION_PLANS)[number]) || undefined,
      },
    });
  };

  return (
    <Stack spacing={3} sx={{ maxWidth: 720 }}>
      <Typography variant="h5">New onboarding</Typography>
      <Paper sx={{ p: { xs: 3, sm: 5 } }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={3}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="firstName"
                control={control}
                rules={{ required: true }}
                render={({ field }) => <TextField {...field} label="First name" required />}
              />
              <Controller
                name="lastName"
                control={control}
                rules={{ required: true }}
                render={({ field }) => <TextField {...field} label="Last name" required />}
              />
            </Stack>

            <Controller
              name="email"
              control={control}
              rules={{ required: true, pattern: /^\S+@\S+\.\S+$/ }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  required
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error ? 'Please enter a valid email address' : ''}
                />
              )}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="contactNumber"
                control={control}
                render={({ field }) => <TextField {...field} label="Contact number" />}
              />
              <Controller
                name="occupation"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Occupation">
                    <MenuItem value="">-</MenuItem>
                    {OCCUPATIONS.map((o) => (
                      <MenuItem key={o} value={o}>
                        {o}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="ahpraNumber"
                control={control}
                render={({ field }) => <TextField {...field} label="AHPRA number" />}
              />
              <Controller
                name="prescriberNumber"
                control={control}
                render={({ field }) => <TextField {...field} label="Prescriber number" />}
              />
            </Stack>

            <Controller
              name="primaryPracticeName"
              control={control}
              render={({ field }) => <TextField {...field} label="Primary medical practice" />}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Controller
                name="plan"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Subscription plan">
                    <MenuItem value="">-</MenuItem>
                    <MenuItem value="essential">Essential ($39.95/month)</MenuItem>
                    <MenuItem value="professional">Professional ($69.95/month)</MenuItem>
                  </TextField>
                )}
              />
              <Controller
                name="ttlDays"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Link expires in (days)"
                    type="number"
                    inputProps={{ min: 1, max: 365 }}
                  />
                )}
              />
            </Stack>

            <Controller
              name="sendEmail"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Send magic-link email immediately via Mandrill"
                />
              )}
            />

            {mutation.isError && (
              <Alert severity="error">{(mutation.error as Error).message}</Alert>
            )}

            <Stack direction="row" spacing={2}>
              <Button onClick={() => navigate('/onboardings')}>Cancel</Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!formState.isValid || mutation.isPending}
              >
                {mutation.isPending ? 'Creating...' : 'Create onboarding'}
              </Button>
            </Stack>
          </Stack>
        </form>
      </Paper>
    </Stack>
  );
};
